// Guards the hand-off to Cognito's hosted login page.
//
// A Cognito client whose Auth URL points back at this app (a misconfiguration
// that has happened locally) sends the browser to a page of ours, which asks
// for the hosted login URL again, which sends it back - forever. Two checks
// stop that: never "redirect to Cognito" on our own origin, and never twice
// within a short window without having come back with a code.

const BREADCRUMB_KEY = 'auth.hostedLoginRedirectAt'
const LOOP_WINDOW_MS = 30 * 1000

const readBreadcrumb = () => {
  try {
    return Number(window.sessionStorage.getItem(BREADCRUMB_KEY)) || 0
  } catch {
    return 0
  }
}

const writeBreadcrumb = (value) => {
  try {
    if (value) {
      window.sessionStorage.setItem(BREADCRUMB_KEY, String(value))
    } else {
      window.sessionStorage.removeItem(BREADCRUMB_KEY)
    }
  } catch {
    // Storage blocked: the same-origin check still applies
  }
}

// A path within this app, such as the server's "/vehicles" for a user who is
// already signed in. Never a hand-off to an identity provider.
export const isInAppPath = (url) =>
  typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')

// True when `url` leads off this app, i.e. is plausibly an identity provider.
export const isExternalUrl = (url) => {
  try {
    return new URL(url, window.location.href).origin !== window.location.origin
  } catch {
    return false
  }
}

// Navigates to the hosted login page and returns true, or returns false (and
// stays put) when doing so would loop. The caller then shows an error instead.
export const redirectToHostedLogin = (url) => {
  if (!isExternalUrl(url)) return false
  if (Date.now() - readBreadcrumb() < LOOP_WINDOW_MS) return false
  writeBreadcrumb(Date.now())
  window.location.href = url
  return true
}

// Called once Cognito has sent the user back with a code and sign-in completed.
export const clearHostedLoginBreadcrumb = () => writeBreadcrumb(null)
